/**
 * Catalog of analytics event names used across the app.
 * Centralizing names prevents typos and gives a single source-of-truth.
 *
 * Naming convention: snake_case, `<domain>_<action>` (past tense for completed
 * actions, present for views).
 */

export const EVENTS = {
  // Lifecycle
  APP_STARTED: 'app_started',
  PAGE_VIEW: 'page_view',

  // Auth
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  LOGOUT: 'logout',
  AUTH_LOGOUT_FORCED: 'auth_logout_forced',
  PERMISSION_DENIED: 'permission_denied',

  // Data layer (auto-instrumented)
  API_ERROR: 'api_error',
  SLOW_QUERY: 'slow_query',
  REPORT_OPENED: 'report_opened',
  REPORT_EMPTY: 'report_empty',
  RESOURCE_CREATED: 'resource_created',
  RESOURCE_UPDATED: 'resource_updated',
  RESOURCE_DELETED: 'resource_deleted',

  // Manual + onboarding
  MANUAL_OPENED: 'manual_opened',
  MANUAL_PAGE_VIEWED: 'manual_page_viewed',
  MANUAL_LINK_CLICKED: 'manual_link_clicked',
  FEATURE_INTRO_SHOWN: 'feature_intro_shown',
  FEATURE_INTRO_DISMISSED: 'feature_intro_dismissed',

  // Dashboards
  DASHBOARD_OPENED: 'dashboard_opened',
  DASHBOARD_GROUPBY_CHANGED: 'dashboard_groupby_changed',

  // Domain-critical (manual instrumentation)
  POINTS_ADDED_MANUAL: 'points_added_manual',
  POINTS_ADDED_BY_REFUEL: 'points_added_by_refuel',
  POINTS_IMPORTED: 'points_imported',
  COUPON_VALIDATED: 'coupon_validated',
  COUPON_REFUNDED: 'coupon_refunded',
  COUPON_UNREDEEMED: 'coupon_unredeemed',
  FUEL_PRICES_EDITED: 'fuel_prices_edited',
  DEFAULT_POINTS_EDITED: 'default_points_edited',
  MINIMUM_AMOUNT_RULE_EDITED: 'minimum_amount_rule_edited',
  POPUP_PUBLISHED: 'popup_published',
  APP_LABEL_EDITED: 'app_label_edited',
  BULK_SEND_DISPATCHED: 'bulk_send_dispatched',
  RECURRING_SEND_CREATED: 'recurring_send_created',
  UNIFIED_SEND_CREATED: 'unified_send_created',
  ROLETA_PUBLISHED: 'roleta_published',
  ROLETA_ACTIVATED: 'roleta_activated',
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/** Threshold (ms) above which a successful query emits SLOW_QUERY. */
export const SLOW_QUERY_THRESHOLD_MS = 5000;
